import React, { useContext, useState, useCallback } from "react";
import { ModalContext } from "context/ModalContext";
import { api_srv } from "services";
import { show_toast } from "utils/helpers";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { User, Mail, Key, Shield } from "react-feather";

export default function CreateUserForm({ onHide, onUserCreated }) {
  const [isLoading, setIsLoading] = useState(false);
  const { modalClose } = useContext(ModalContext);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    username: Yup.string().required("Username is required"),
    isSuperAdmin: Yup.boolean().required("Super admin status is required"),
    roleType: Yup.string()
      .oneOf(['maker', 'checker', 'ace'], 'Invalid role type')
      .required("Role type is required"),
    password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
    externalRef: Yup.string().optional(),
  });

  const handleSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const newUser = {
          id: Date.now(),
          ...values,
          status: 'active',
          role: values.roleType, // Assuming roleType maps to role
          createdDate: new Date().toISOString(),
          isLoggedIn: false
        };

        show_toast("User created successfully", "success");
        onUserCreated(newUser);
        modalClose();
        resetForm();
      } catch (err) {
        show_toast(`Failed to create user: ${err.message}`, "error");
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
    [onUserCreated, modalClose]
  );

  return (
    <div className="container-fluid py-3">
      <div className="card shadow-sm p-4">
        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            isSuperAdmin: false,
            roleType: 'maker',
            password: '',
            externalRef: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form className="needs-validation" noValidate>
              {/* User Information */}
              <div className="mb-4 pb-4 border-bottom">
                <h5 className="mb-3 d-flex align-items-center">
                  <User size={20} className="mr-2" style={{ color: '#10b981' }} />
                  User Details
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="firstName">First Name</label>
                    <Field name="firstName" className="form-control" />
                    <ErrorMessage name="firstName" component="div" className="text-danger small" />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="lastName">Last Name</label>
                    <Field name="lastName" className="form-control" />
                    <ErrorMessage name="lastName" component="div" className="text-danger small" />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="email">Email</label>
                    <Field name="email" type="email" className="form-control" />
                    <ErrorMessage name="email" component="div" className="text-danger small" />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="username">Username</label>
                    <Field name="username" className="form-control" />
                    <ErrorMessage name="username" component="div" className="text-danger small" />
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div className="mb-4 pb-4 border-bottom">
                <h5 className="mb-3 d-flex align-items-center">
                  <Shield size={20} className="mr-2" style={{ color: '#8b5cf6' }} />
                  Permissions
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label>Is Super Admin?</label>
                    <div className="d-flex" style={{ gap: '2rem' }}>
                      <label>
                        <Field type="radio" name="isSuperAdmin" value={true} onChange={handleChange} />
                        Yes
                      </label>
                      <label>
                        <Field type="radio" name="isSuperAdmin" value={false} onChange={handleChange} />
                        No
                      </label>
                    </div>
                    <ErrorMessage name="isSuperAdmin" component="div" className="text-danger small" />
                  </div>
                  <div className="col-md-6">
                    <label>Role Type</label>
                    <div className="d-flex" style={{ gap: '2rem' }}>
                      <label>
                        <Field type="radio" name="roleType" value="maker" onChange={handleChange} />
                        Maker
                      </label>
                      <label>
                        <Field type="radio" name="roleType" value="checker" onChange={handleChange} />
                        Checker
                      </label>
                      <label>
                        <Field type="radio" name="roleType" value="ace" onChange={handleChange} />
                        Ace
                      </label>
                    </div>
                    <ErrorMessage name="roleType" component="div" className="text-danger small" />
                  </div>
                </div>
              </div>

              {/* Security */}
              <div className="mb-4">
                <h5 className="mb-3 d-flex align-items-center">
                  <Key size={20} className="mr-2" style={{ color: '#ef4444' }} />
                  Security
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="password">Password</label>
                    <Field name="password" type="password" className="form-control" />
                    <ErrorMessage name="password" component="div" className="text-danger small" />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="externalRef">External Reference</label>
                    <Field name="externalRef" className="form-control" />
                    <ErrorMessage name="externalRef" component="div" className="text-danger small" />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end" style={{ gap: '2rem' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={modalClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="btn btn-primary d-flex align-items-center"
                >
                  {isLoading || isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    'Save User'
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}